import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '../../../../lib/useMediaQuery';
import {
  EMPTY_WITH_HIDDEN_SUBREGIONS,
  initialCalendarChoices,
  SubregionLegend,
  SubregionPill,
  useCalendarState,
  useSubregionFilter,
  WorkshopsCalendar,
  type WorkshopChip,
} from '../../primitives';
import { BucketError } from '../../regional/BucketStates';
import { teacherChipDetailUrl } from '../../workshopDetailUrlFor';
import { useFillViewport } from '../use-fill-viewport';
import { useMyWorkshops } from '../use-my-workshops';

/** Where the calendar switches to opening on the week rather than the month. */
const NARROW = '(max-width: 1279px)';
/** Kept clear below the calendar: the page's 28px bottom padding plus the
 *  frame's borders, so a calendar that fits leaves the page unscrolled. */
const PAGE_BOTTOM_PX = 30;
const NO_WORKSHOPS: WorkshopChip[] = [];

/** My schedule above phone width: one calendar of the teacher's own workshops.
 *  It fills the screen at least; a taller month scrolls the page, never the card. */
export default function TeacherOverviewDesktop() {
  const navigate = useNavigate();
  const narrow = useMediaQuery(NARROW);
  const choices = useState(() =>
    initialCalendarChoices({ initialView: narrow ? 'Week' : 'Month' }),
  );
  const calendar = useCalendarState(choices);
  const q = useMyWorkshops(calendar.range.from, calendar.range.to);
  const fill = useFillViewport<HTMLDivElement>(PAGE_BOTTOM_PX);
  const filter = useSubregionFilter(q.data ?? NO_WORKSHOPS, calendar.hidden);

  if (q.isError) return <BucketError error={q.error} />;

  return (
    <div
      ref={fill.ref}
      style={{ minHeight: fill.height, display: 'flex', flexDirection: 'column' }}
    >
      <WorkshopsCalendar
        workshops={filter.shown}
        view={calendar.view}
        onViewChange={calendar.setView}
        anchor={calendar.anchor}
        onNav={calendar.nav}
        showCancelled={calendar.showCancelled}
        onToggleCancelled={calendar.toggleCancelled}
        filters={
          <SubregionLegend>
            {filter.subregions.map((s) => (
              <SubregionPill
                key={s.label}
                label={s.label}
                color={s.color}
                hidden={filter.hidden.has(s.label)}
                onToggle={() => calendar.toggleSubregion(s.label)}
              />
            ))}
          </SubregionLegend>
        }
        emptyLabel={filter.hidden.size > 0 ? EMPTY_WITH_HIDDEN_SUBREGIONS : undefined}
        title="My workshops"
        fill
        onWorkshopClick={(w) => navigate(teacherChipDetailUrl(w))}
      />
    </div>
  );
}
